import React, { useState } from 'react';
import { FaSearch, FaTimes } from 'react-icons/fa';

/**
 * Modular search input component with search icon and instant clear-to-empty button.
 * Supports both controlled (`value`) and uncontrolled (`defaultValue`) states.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.value] - Controlled search query string.
 * @param {string} [props.defaultValue=''] - Initial query string for uncontrolled usage.
 * @param {Function} [props.onChange] - Callback invoked when the query changes: `(nextValue) => void`.
 * @param {Function} [props.onSearchChange] - Callback invoked when the query changes: `(nextValue) => void`.
 * @param {string} [props.placeholder='Search...'] - Placeholder text displayed inside the search input.
 * @param {string} [props.className=''] - Additional CSS classes applied to the container element.
 * @param {string} [props.maxWidth='max-w-sm'] - Tailwind max-width class (e.g., 'max-w-sm', 'max-w-xs', 'max-w-md').
 *
 * @example
 * <DataSearch
 *   placeholder="Search..."
 *   onSearchChange={(query) => handleSearch(query)}
 * />
 */
const DataSearch = ({
  value,
  defaultValue = '',
  onChange,
  onSearchChange,
  placeholder = 'Search...',
  className = '',
  maxWidth = 'max-w-sm'
}) => {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue);

  let activeValue = internalValue;
  if (isControlled) {
    activeValue = value;
  }

  const handleInputChange = (event) => {
    const nextValue = event.target.value;

    if (!isControlled) {
      setInternalValue(nextValue);
    }

    if (typeof onSearchChange === 'function') {
      onSearchChange(nextValue);
    }

    if (typeof onChange === 'function') {
      onChange(nextValue);
    }
  };

  const handleClear = () => {
    if (!isControlled) {
      setInternalValue('');
    }

    if (typeof onSearchChange === 'function') {
      onSearchChange('');
    }

    if (typeof onChange === 'function') {
      onChange('');
    }
  };

  const hasValue = Boolean(activeValue);

  return (
    <div className={`relative flex-1 ${maxWidth} ${className}`.trim()}>
      <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-aimhi-textlight-neutral text-xs" />
      <input
        type="text"
        value={activeValue}
        onChange={handleInputChange}
        placeholder={placeholder}
        className="w-full rounded-md border border-aimhi-textlight-neutral bg-white pl-9 pr-7 py-2 text-sm text-gray-900 placeholder:not-italic placeholder:text-aimhi-textlight-neutral/70 focus:border-[#3B74BE] focus:outline-none focus:ring-1 focus:ring-[#3B74BE] shadow-2xs"
      />
      {hasValue && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
          title="Clear search"
          aria-label="Clear search"
        >
          <FaTimes className="text-xs" />
        </button>
      )}
    </div>
  );
};

export default DataSearch;
